import React, { ReactNode, useId, useState } from 'react';

import { cn } from '@cineamo/ui/src/helpers/cn';

import { IconDown16 } from '@cineamo/ui/src/icons/ic-down';

/**
 * Generic collapsible section component with expand/collapse functionality.
 * Can be used for dropdowns, accordions, or any collapsible content.
 * Reusable across different apps with customizable styling.
 */
const CollapsibleSection: React.FC<{
    children: ReactNode;
    className?: string;
    toggleAreaClassName?: string;
    contentClassName?: string;
    title: string | React.JSX.Element;
    leadingIcon?: React.JSX.Element;
    initiallyExpanded?: boolean;
    /** Called whenever the section is expanded/collapsed. Receives the new expanded state. */
    onToggle?: (expanded: boolean) => void;
}> = ({
    children,
    className,
    toggleAreaClassName,
    contentClassName,
    title,
    leadingIcon,
    initiallyExpanded = false,
    onToggle
}) => {
    const [expand, setExpand] = useState<boolean>(initiallyExpanded);
    const contentId = useId();

    function toggleExpand() {
        const nextExpand = !expand;
        setExpand(nextExpand);
        onToggle?.(nextExpand);
    }

    return (
        <div className={cn(className, 'flex flex-col')}>
            <button
                type="button"
                className={cn(
                    toggleAreaClassName,
                    'flex flex-row items-center justify-between cursor-pointer select-none space-x-8 py-16 w-full text-left bg-transparent border-none'
                )}
                onClick={toggleExpand}
                aria-expanded={expand}
                aria-controls={contentId}>
                {leadingIcon || null}

                <h2 className="text-body-bold-16">{title}</h2>

                <IconDown16
                    className={cn('flex-none transition-all transform', expand && '-rotate-180')}
                    aria-hidden="true"
                />
            </button>

            <div
                id={contentId}
                role="region"
                aria-hidden={!expand}
                className={cn(
                    'flex flex-col',
                    'text-body-14 lg:text-body-16',
                    'transform transition-all duration-150 overflow-y-auto',
                    expand === true ? 'max-h-[calc(100vh-128px)]' : 'max-h-0 h-0',
                    expand === true ? 'mb-32 mt-16' : 'mb-0 mt-0',
                    contentClassName
                )}>
                {children}
            </div>
        </div>
    );
};

export default CollapsibleSection;
